import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ScatterChart, Scatter, ZAxis } from 'recharts';
const LineLevelPerformanceDashboard: React.FC = () => {
  // KPI Data
  const kpis = [{
    title: 'Cycle Time vs Standard',
    value: '98.5%',
    change: '+1.2%',
    isPositive: true,
    subtitle: 'Avg across lines'
  }, {
    title: 'Changeover Time',
    value: '18.5 min',
    change: '-2.3 min',
    isPositive: true,
    subtitle: 'Avg per changeover'
  }, {
    title: 'Operator Efficiency',
    value: '94.2%',
    change: '+3.5%',
    isPositive: true,
    subtitle: 'Overall score'
  }];
  // Line Performance Radar Data
  const linePerformanceRadarData = [{
    metric: 'Cycle Time',
    Line1: 95,
    Line2: 98,
    Line3: 92,
    Line4: 96,
    Line5: 94
  }, {
    metric: 'Quality',
    Line1: 96,
    Line2: 97,
    Line3: 95,
    Line4: 96,
    Line5: 96
  }, {
    metric: 'OEE',
    Line1: 76,
    Line2: 82,
    Line3: 75,
    Line4: 79,
    Line5: 78
  }, {
    metric: 'Uptime',
    Line1: 87,
    Line2: 91,
    Line3: 86,
    Line4: 89,
    Line5: 88
  }, {
    metric: 'Operator Efficiency',
    Line1: 92,
    Line2: 96,
    Line3: 90,
    Line4: 94,
    Line5: 93
  }, {
    metric: 'Changeover Speed',
    Line1: 88,
    Line2: 92,
    Line3: 85,
    Line4: 90,
    Line5: 89
  }];
  // Changeover Trend Data (Last 30 Days)
  const changeoverTrendData = [{
    day: 'Day 1',
    Line1: 22,
    Line2: 18,
    Line3: 25,
    Line4: 20,
    Line5: 21,
    target: 20
  }, {
    day: 'Day 3',
    Line1: 21,
    Line2: 17,
    Line3: 24,
    Line4: 19,
    Line5: 20,
    target: 20
  }, {
    day: 'Day 5',
    Line1: 20,
    Line2: 16,
    Line3: 23,
    Line4: 18,
    Line5: 19,
    target: 20
  }, {
    day: 'Day 7',
    Line1: 19,
    Line2: 15,
    Line3: 22,
    Line4: 17,
    Line5: 18,
    target: 20
  }, {
    day: 'Day 9',
    Line1: 18,
    Line2: 14,
    Line3: 21,
    Line4: 16,
    Line5: 17,
    target: 20
  }, {
    day: 'Day 11',
    Line1: 17,
    Line2: 13,
    Line3: 20,
    Line4: 15,
    Line5: 16,
    target: 20
  }, {
    day: 'Day 13',
    Line1: 16,
    Line2: 12,
    Line3: 19,
    Line4: 14,
    Line5: 15,
    target: 20
  }, {
    day: 'Day 15',
    Line1: 15,
    Line2: 11,
    Line3: 18,
    Line4: 13,
    Line5: 14,
    target: 20
  }, {
    day: 'Day 17',
    Line1: 16,
    Line2: 12,
    Line3: 19,
    Line4: 14,
    Line5: 15,
    target: 20
  }, {
    day: 'Day 19',
    Line1: 17,
    Line2: 13,
    Line3: 20,
    Line4: 15,
    Line5: 16,
    target: 20
  }, {
    day: 'Day 21',
    Line1: 18,
    Line2: 14,
    Line3: 21,
    Line4: 16,
    Line5: 17,
    target: 20
  }, {
    day: 'Day 23',
    Line1: 19,
    Line2: 15,
    Line3: 22,
    Line4: 17,
    Line5: 18,
    target: 20
  }, {
    day: 'Day 25',
    Line1: 18,
    Line2: 14,
    Line3: 21,
    Line4: 16,
    Line5: 17,
    target: 20
  }, {
    day: 'Day 27',
    Line1: 17,
    Line2: 13,
    Line3: 20,
    Line4: 15,
    Line5: 16,
    target: 20
  }, {
    day: 'Day 29',
    Line1: 16,
    Line2: 12,
    Line3: 19,
    Line4: 14,
    Line5: 15,
    target: 20
  }, {
    day: 'Day 30',
    Line1: 15,
    Line2: 11,
    Line3: 18,
    Line4: 13,
    Line5: 14,
    target: 20
  }];
  // Operator Productivity Chart Data
  const operatorProductivityData = [{
    operator: 'Team A',
    efficiency: 96,
    output: 4200,
    quality: 98,
    experience: 5
  }, {
    operator: 'Team B',
    efficiency: 94,
    output: 4100,
    quality: 97,
    experience: 4
  }, {
    operator: 'Team C',
    efficiency: 92,
    output: 3950,
    quality: 96,
    experience: 3
  }, {
    operator: 'Team D',
    efficiency: 95,
    output: 4150,
    quality: 97,
    experience: 4
  }, {
    operator: 'Team E',
    efficiency: 93,
    output: 4050,
    quality: 96,
    experience: 3
  }, {
    operator: 'Team F',
    efficiency: 91,
    output: 3900,
    quality: 95,
    experience: 2
  }, {
    operator: 'Team G',
    efficiency: 97,
    output: 4250,
    quality: 98,
    experience: 6
  }, {
    operator: 'Team H',
    efficiency: 90,
    output: 3850,
    quality: 95,
    experience: 2
  }];
  // Cycle Time Analysis Data
  const cycleTimeAnalysisData = [{
    line: 'Line 1',
    standard: 45,
    actual: 43,
    variance: -2,
    efficiency: 104.7
  }, {
    line: 'Line 2',
    standard: 45,
    actual: 42,
    variance: -3,
    efficiency: 107.1
  }, {
    line: 'Line 3',
    standard: 45,
    actual: 47,
    variance: 2,
    efficiency: 95.7
  }, {
    line: 'Line 4',
    standard: 45,
    actual: 44,
    variance: -1,
    efficiency: 102.3
  }, {
    line: 'Line 5',
    standard: 45,
    actual: 45,
    variance: 0,
    efficiency: 100.0
  }];
  // Changeover Summary by Line
  const changeoverSummaryData = [{
    line: 'Line 1',
    avgTime: 15,
    bestTime: 12,
    worstTime: 22,
    frequency: 45,
    target: 20
  }, {
    line: 'Line 2',
    avgTime: 11,
    bestTime: 9,
    worstTime: 18,
    frequency: 42,
    target: 20
  }, {
    line: 'Line 3',
    avgTime: 18,
    bestTime: 15,
    worstTime: 25,
    frequency: 48,
    target: 20
  }, {
    line: 'Line 4',
    avgTime: 13,
    bestTime: 10,
    worstTime: 20,
    frequency: 44,
    target: 20
  }, {
    line: 'Line 5',
    avgTime: 14,
    bestTime: 11,
    worstTime: 21,
    frequency: 46,
    target: 20
  }];
  // Line Performance Table Data
  const linePerformanceTableData = [{
    line: 'Line 1',
    shift: 'Shift A',
    cycleTime: 43,
    standardTime: 45,
    cycleEfficiency: 104.7,
    changeoverTime: 15,
    changeoverCount: 3,
    operatorEfficiency: 92,
    output: 3750,
    target: 3667,
    oee: 78.5,
    uptime: 87.2,
    quality: 96.0,
    scrapRate: 1.8
  }, {
    line: 'Line 1',
    shift: 'Shift B',
    cycleTime: 42,
    standardTime: 45,
    cycleEfficiency: 107.1,
    changeoverTime: 14,
    changeoverCount: 2,
    operatorEfficiency: 96,
    output: 3900,
    target: 3667,
    oee: 82.3,
    uptime: 89.5,
    quality: 97.5,
    scrapRate: 1.5
  }, {
    line: 'Line 1',
    shift: 'Shift C',
    cycleTime: 45,
    standardTime: 45,
    cycleEfficiency: 100.0,
    changeoverTime: 18,
    changeoverCount: 4,
    operatorEfficiency: 88,
    output: 3450,
    target: 3666,
    oee: 72.8,
    uptime: 84.5,
    quality: 94.2,
    scrapRate: 2.1
  }, {
    line: 'Line 2',
    shift: 'Shift A',
    cycleTime: 42,
    standardTime: 45,
    cycleEfficiency: 107.1,
    changeoverTime: 11,
    changeoverCount: 2,
    operatorEfficiency: 94,
    output: 3850,
    target: 3667,
    oee: 80.2,
    uptime: 90.5,
    quality: 97.0,
    scrapRate: 1.6
  }, {
    line: 'Line 2',
    shift: 'Shift B',
    cycleTime: 41,
    standardTime: 45,
    cycleEfficiency: 109.8,
    changeoverTime: 10,
    changeoverCount: 2,
    operatorEfficiency: 98,
    output: 4050,
    target: 3667,
    oee: 85.8,
    uptime: 92.8,
    quality: 98.2,
    scrapRate: 1.3
  }, {
    line: 'Line 2',
    shift: 'Shift C',
    cycleTime: 43,
    standardTime: 45,
    cycleEfficiency: 104.7,
    changeoverTime: 13,
    changeoverCount: 3,
    operatorEfficiency: 90,
    output: 3600,
    target: 3666,
    oee: 76.5,
    uptime: 88.2,
    quality: 96.5,
    scrapRate: 1.9
  }, {
    line: 'Line 3',
    shift: 'Shift A',
    cycleTime: 47,
    standardTime: 45,
    cycleEfficiency: 95.7,
    changeoverTime: 18,
    changeoverCount: 4,
    operatorEfficiency: 89,
    output: 3450,
    target: 3667,
    oee: 73.2,
    uptime: 86.0,
    quality: 95.0,
    scrapRate: 2.3
  }, {
    line: 'Line 3',
    shift: 'Shift B',
    cycleTime: 46,
    standardTime: 45,
    cycleEfficiency: 97.8,
    changeoverTime: 17,
    changeoverCount: 3,
    operatorEfficiency: 91,
    output: 3550,
    target: 3667,
    oee: 75.8,
    uptime: 87.5,
    quality: 96.0,
    scrapRate: 2.0
  }, {
    line: 'Line 3',
    shift: 'Shift C',
    cycleTime: 49,
    standardTime: 45,
    cycleEfficiency: 91.8,
    changeoverTime: 20,
    changeoverCount: 5,
    operatorEfficiency: 85,
    output: 3200,
    target: 3666,
    oee: 68.5,
    uptime: 82.8,
    quality: 93.5,
    scrapRate: 2.6
  }, {
    line: 'Line 4',
    shift: 'Shift A',
    cycleTime: 44,
    standardTime: 45,
    cycleEfficiency: 102.3,
    changeoverTime: 13,
    changeoverCount: 3,
    operatorEfficiency: 93,
    output: 3700,
    target: 3667,
    oee: 77.8,
    uptime: 88.5,
    quality: 96.5,
    scrapRate: 1.7
  }, {
    line: 'Line 4',
    shift: 'Shift B',
    cycleTime: 43,
    standardTime: 45,
    cycleEfficiency: 104.7,
    changeoverTime: 12,
    changeoverCount: 2,
    operatorEfficiency: 95,
    output: 3850,
    target: 3667,
    oee: 81.5,
    uptime: 90.2,
    quality: 97.2,
    scrapRate: 1.5
  }, {
    line: 'Line 4',
    shift: 'Shift C',
    cycleTime: 45,
    standardTime: 45,
    cycleEfficiency: 100.0,
    changeoverTime: 15,
    changeoverCount: 4,
    operatorEfficiency: 89,
    output: 3400,
    target: 3666,
    oee: 71.2,
    uptime: 85.5,
    quality: 95.0,
    scrapRate: 2.2
  }, {
    line: 'Line 5',
    shift: 'Shift A',
    cycleTime: 45,
    standardTime: 45,
    cycleEfficiency: 100.0,
    changeoverTime: 14,
    changeoverCount: 3,
    operatorEfficiency: 91,
    output: 3600,
    target: 3667,
    oee: 76.2,
    uptime: 87.8,
    quality: 96.2,
    scrapRate: 1.9
  }, {
    line: 'Line 5',
    shift: 'Shift B',
    cycleTime: 44,
    standardTime: 45,
    cycleEfficiency: 102.3,
    changeoverTime: 13,
    changeoverCount: 3,
    operatorEfficiency: 94,
    output: 3750,
    target: 3667,
    oee: 79.5,
    uptime: 89.2,
    quality: 96.8,
    scrapRate: 1.7
  }, {
    line: 'Line 5',
    shift: 'Shift C',
    cycleTime: 46,
    standardTime: 45,
    cycleEfficiency: 97.8,
    changeoverTime: 16,
    changeoverCount: 4,
    operatorEfficiency: 87,
    output: 3350,
    target: 3666,
    oee: 70.8,
    uptime: 84.8,
    quality: 94.8,
    scrapRate: 2.3
  }];
  const getCycleEfficiencyColor = (efficiency: number) => {
    if (efficiency >= 105) return 'bg-green-100 text-green-700';
    if (efficiency >= 100) return 'bg-blue-100 text-blue-700';
    if (efficiency >= 95) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };
  const getChangeoverColor = (time: number) => {
    if (time <= 12) return 'bg-green-100 text-green-700';
    if (time <= 15) return 'bg-blue-100 text-blue-700';
    if (time <= 18) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };
  return <div data-id="element-5369">
      <DescriptionPanel title="Line-Level Performance Analysis" description="Detailed performance metrics for each production line including cycle times, changeover efficiency, operator productivity, and quality metrics. Identify best practices and optimization opportunities at the line level." insights={['Line 2 consistently outperforming with 107.1% cycle efficiency and 11-min avg changeover', 'Shift B showing best operator efficiency at 96% - training model to replicate', 'Line 3 requires attention with 95.7% cycle efficiency and 18-min changeover times', 'Overall changeover time reduced by 2.3 minutes through SMED implementation']} data-id="element-5370" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-5371">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-5372">
          Line Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-5373">
          {kpis.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-5374">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-5375">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-2" data-id="element-5376">{kpi.subtitle}</div>
              <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-5377">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-5378">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-5379" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-5380" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Line Performance Radar */}
      <Card className="p-6 mb-8" data-id="element-5381">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5382">
          Line Performance Radar - Multi-Metric Comparison
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-5383">
          Comprehensive performance view across 6 key metrics for all production
          lines
        </p>
        <ResponsiveContainer width="100%" height={450} data-id="element-5384">
          <RadarChart data={linePerformanceRadarData} data-id="element-5385">
            <PolarGrid stroke="#e5e7eb" data-id="element-5386" />
            <PolarAngleAxis dataKey="metric" tick={{
            fontSize: 11
          }} data-id="element-5387" />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{
            fontSize: 10
          }} data-id="element-5388" />
            <Radar name="Line 1" dataKey="Line1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} data-id="element-5389" />
            <Radar name="Line 2" dataKey="Line2" stroke="#10b981" fill="#10b981" fillOpacity={0.2} data-id="element-5390" />
            <Radar name="Line 3" dataKey="Line3" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.2} data-id="element-5391" />
            <Radar name="Line 4" dataKey="Line4" stroke="#ef4444" fill="#ef4444" fillOpacity={0.2} data-id="element-5392" />
            <Radar name="Line 5" dataKey="Line5" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} data-id="element-5393" />
            <Legend data-id="element-5394" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-5395" />
          </RadarChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5396">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-5397">
            <div className="text-center" data-id="element-5398">
              <div className="text-gray-600" data-id="element-5399">Line 1</div>
              <div className="text-lg font-bold text-blue-600" data-id="element-5400">90.0</div>
              <div className="text-xs text-gray-500" data-id="element-5401">avg score</div>
            </div>
            <div className="text-center" data-id="element-5402">
              <div className="text-gray-600" data-id="element-5403">Line 2</div>
              <div className="text-lg font-bold text-green-600" data-id="element-5404">92.7</div>
              <div className="text-xs text-gray-500" data-id="element-5405">best performer</div>
            </div>
            <div className="text-center" data-id="element-5406">
              <div className="text-gray-600" data-id="element-5407">Line 3</div>
              <div className="text-lg font-bold text-orange-600" data-id="element-5408">87.2</div>
              <div className="text-xs text-gray-500" data-id="element-5409">needs improvement</div>
            </div>
            <div className="text-center" data-id="element-5410">
              <div className="text-gray-600" data-id="element-5411">Line 4</div>
              <div className="text-lg font-bold text-red-600" data-id="element-5412">90.7</div>
              <div className="text-xs text-gray-500" data-id="element-5413">avg score</div>
            </div>
            <div className="text-center" data-id="element-5414">
              <div className="text-gray-600" data-id="element-5415">Line 5</div>
              <div className="text-lg font-bold text-purple-600" data-id="element-5416">89.7</div>
              <div className="text-xs text-gray-500" data-id="element-5417">avg score</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Changeover Trend and Cycle Time Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-5418">
        {/* Changeover Trend */}
        <Card className="p-6" data-id="element-5419">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5420">
            Changeover Time Trend (Last 30 Days)
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-5421">
            Average changeover time in minutes per line
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-5422">
            <LineChart data={changeoverTrendData} data-id="element-5423">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5424" />
              <XAxis dataKey="day" tick={{
              fontSize: 10
            }} data-id="element-5425" />
              <YAxis tick={{
              fontSize: 11
            }} domain={[0, 30]} data-id="element-5426" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value} min`} data-id="element-5427" />
              <Legend data-id="element-5428" />
              <Line type="monotone" dataKey="Line1" stroke="#3b82f6" strokeWidth={2} name="Line 1" dot={{
              r: 2
            }} data-id="element-5429" />
              <Line type="monotone" dataKey="Line2" stroke="#10b981" strokeWidth={2} name="Line 2" dot={{
              r: 2
            }} data-id="element-5430" />
              <Line type="monotone" dataKey="Line3" stroke="#f59e0b" strokeWidth={2} name="Line 3" dot={{
              r: 2
            }} data-id="element-5431" />
              <Line type="monotone" dataKey="Line4" stroke="#ef4444" strokeWidth={2} name="Line 4" dot={{
              r: 2
            }} data-id="element-5432" />
              <Line type="monotone" dataKey="Line5" stroke="#8b5cf6" strokeWidth={2} name="Line 5" dot={{
              r: 2
            }} data-id="element-5433" />
              <Line type="monotone" dataKey="target" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" name="Target" dot={false} data-id="element-5434" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5435">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-5436">
              <div className="text-center" data-id="element-5437">
                <div className="text-gray-600" data-id="element-5438">Best Line</div>
                <div className="text-xl font-bold text-green-600" data-id="element-5439">Line 2</div>
                <div className="text-xs text-gray-500" data-id="element-5440">11 min avg</div>
              </div>
              <div className="text-center" data-id="element-5441">
                <div className="text-gray-600" data-id="element-5442">Target</div>
                <div className="text-xl font-bold text-gray-900" data-id="element-5443">20 min</div>
                <div className="text-xs text-gray-500" data-id="element-5444">all below target</div>
              </div>
              <div className="text-center" data-id="element-5445">
                <div className="text-gray-600" data-id="element-5446">Improvement</div>
                <div className="text-xl font-bold text-blue-600" data-id="element-5447">-2.3 min</div>
                <div className="text-xs text-gray-500" data-id="element-5448">vs last month</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Cycle Time Analysis */}
        <Card className="p-6" data-id="element-5449">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5450">
            Cycle Time vs Standard Time
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-5451">
            Actual vs standard cycle time in seconds
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-5452">
            <BarChart data={cycleTimeAnalysisData} data-id="element-5453">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5454" />
              <XAxis dataKey="line" tick={{
              fontSize: 11
            }} data-id="element-5455" />
              <YAxis tick={{
              fontSize: 11
            }} domain={[0, 50]} data-id="element-5456" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value} sec`} data-id="element-5457" />
              <Legend data-id="element-5458" />
              <Bar dataKey="standard" fill="#94a3b8" name="Standard Time" data-id="element-5459" />
              <Bar dataKey="actual" fill="#3b82f6" name="Actual Time" data-id="element-5460" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5461">
            <div className="space-y-2" data-id="element-5462">
              {cycleTimeAnalysisData.map((item, index) => <div key={index} className="flex justify-between items-center text-sm" data-id="element-5463">
                  <span className="text-gray-700 font-medium" data-id="element-5464">{item.line}</span>
                  <div className="flex items-center gap-4" data-id="element-5465">
                    <span className="text-gray-600" data-id="element-5466">
                      {item.actual}s / {item.standard}s
                    </span>
                    <span className={`font-bold ${item.efficiency >= 100 ? 'text-green-600' : 'text-orange-600'}`} data-id="element-5467">
                      {item.efficiency.toFixed(1)}%
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </Card>
      </div>
      {/* Operator Productivity Chart */}
      <Card className="p-6 mb-8" data-id="element-5468">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5469">
          Operator Productivity Analysis
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-5470">
          Efficiency vs output by operator team (bubble size = experience level)
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-5471">
          <ScatterChart data-id="element-5472">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5473" />
            <XAxis type="number" dataKey="efficiency" name="Efficiency" unit="%" domain={[88, 100]} tick={{
            fontSize: 11
          }} label={{
            value: 'Operator Efficiency (%)',
            position: 'insideBottom',
            offset: -5,
            style: {
              fontSize: 12
            }
          }} data-id="element-5474" />
            <YAxis type="number" dataKey="output" name="Output" unit=" units" domain={[3800, 4300]} tick={{
            fontSize: 11
          }} label={{
            value: 'Daily Output (Units)',
            angle: -90,
            position: 'insideLeft',
            style: {
              fontSize: 12
            }
          }} data-id="element-5475" />
            <ZAxis type="number" dataKey="experience" range={[100, 1000]} name="Experience" data-id="element-5476" />
            <Tooltip cursor={{
            strokeDasharray: '3 3'
          }} contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: any, name: string) => {
            if (name === 'Efficiency') return `${value}%`;
            if (name === 'Output') return `${value} units`;
            if (name === 'Experience') return `${value} years`;
            return value;
          }} data-id="element-5477" />
            <Legend data-id="element-5478" />
            <Scatter name="Operator Teams" data={operatorProductivityData} fill="#3b82f6" data-id="element-5479" />
          </ScatterChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5480">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-5481">
            <div className="text-center" data-id="element-5482">
              <div className="text-gray-600" data-id="element-5483">Best Team</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5484">Team G</div>
              <div className="text-xs text-gray-500" data-id="element-5485">97% efficiency</div>
            </div>
            <div className="text-center" data-id="element-5486">
              <div className="text-gray-600" data-id="element-5487">Avg Efficiency</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-5488">93.5%</div>
              <div className="text-xs text-gray-500" data-id="element-5489">across all teams</div>
            </div>
            <div className="text-center" data-id="element-5490">
              <div className="text-gray-600" data-id="element-5491">Avg Output</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5492">4,056</div>
              <div className="text-xs text-gray-500" data-id="element-5493">units per team</div>
            </div>
            <div className="text-center" data-id="element-5494">
              <div className="text-gray-600" data-id="element-5495">Quality Score</div>
              <div className="text-xl font-bold text-purple-600" data-id="element-5496">96.5%</div>
              <div className="text-xs text-gray-500" data-id="element-5497">avg quality</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Changeover Summary */}
      <Card className="p-6 mb-8" data-id="element-5498">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5499">
          Changeover Performance Summary
        </h3>
        <div className="space-y-4" data-id="element-5500">
          {changeoverSummaryData.map((item, index) => <div key={index} className="border-b border-gray-100 pb-3" data-id="element-5501">
              <div className="flex items-center justify-between mb-2" data-id="element-5502">
                <span className="text-sm font-semibold text-gray-800" data-id="element-5503">
                  {item.line}
                </span>
                <div className="flex items-center gap-6 text-sm" data-id="element-5504">
                  <div className="text-center" data-id="element-5505">
                    <div className="text-xs text-gray-600" data-id="element-5506">Avg Time</div>
                    <div className="font-bold text-gray-900" data-id="element-5507">
                      {item.avgTime} min
                    </div>
                  </div>
                  <div className="text-center" data-id="element-5508">
                    <div className="text-xs text-gray-600" data-id="element-5509">Best</div>
                    <div className="font-bold text-green-600" data-id="element-5510">
                      {item.bestTime} min
                    </div>
                  </div>
                  <div className="text-center" data-id="element-5511">
                    <div className="text-xs text-gray-600" data-id="element-5512">Worst</div>
                    <div className="font-bold text-red-600" data-id="element-5513">
                      {item.worstTime} min
                    </div>
                  </div>
                  <div className="text-center" data-id="element-5514">
                    <div className="text-xs text-gray-600" data-id="element-5515">Frequency</div>
                    <div className="font-bold text-gray-900" data-id="element-5516">
                      {item.frequency}/mo
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative h-4 bg-gray-100 rounded overflow-hidden" data-id="element-5517">
                <div className={`h-full ${item.avgTime <= 12 ? 'bg-green-500' : item.avgTime <= 15 ? 'bg-blue-500' : item.avgTime <= 18 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
              width: `${item.avgTime / item.target * 100}%`
            }} data-id="element-5518" />
                <div className="absolute top-0 h-full w-0.5 bg-gray-600" style={{
              left: `${item.target / item.target * 100}%`
            }} data-id="element-5519" />
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-500" data-id="element-5520">
                <span data-id="element-5521">0 min</span>
                <span data-id="element-5522">Target: {item.target} min</span>
              </div>
            </div>)}
        </div>
      </Card>
      {/* Line Performance Table */}
      <Card className="p-6" data-id="element-5523">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5524">
          Detailed Line Performance - Shift Level
        </h3>
        <div className="overflow-x-auto" data-id="element-5525">
          <table className="w-full" data-id="element-5526">
            <thead data-id="element-5527">
              <tr className="border-b-2 border-gray-300" data-id="element-5528">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5529">
                  Line
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5530">
                  Shift
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5531">
                  Cycle Time (s)
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5532">
                  Standard (s)
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5533">
                  Cycle Efficiency %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5534">
                  Changeover (min)
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5535">
                  CO Count
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5536">
                  Operator Eff %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5537">
                  Output
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5538">
                  Target
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5539">
                  OEE %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5540">
                  Uptime %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5541">
                  Quality %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5542">
                  Scrap %
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5543">
              {linePerformanceTableData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-5544">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-5545">
                    {row.line}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-5546">
                    {row.shift}
                  </td>
                  <td className="py-3 px-4 text-center text-sm font-semibold text-gray-900" data-id="element-5547">
                    {row.cycleTime}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-600" data-id="element-5548">
                    {row.standardTime}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5549">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getCycleEfficiencyColor(row.cycleEfficiency)}`} data-id="element-5550">
                      {row.cycleEfficiency.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5551">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getChangeoverColor(row.changeoverTime)}`} data-id="element-5552">
                      {row.changeoverTime}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5553">
                    {row.changeoverCount}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5554">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.operatorEfficiency >= 95 ? 'bg-green-100 text-green-700' : row.operatorEfficiency >= 90 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-5555">
                      {row.operatorEfficiency}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-5556">
                    {row.output.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-5557">
                    {row.target.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5558">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.oee >= 80 ? 'bg-green-100 text-green-700' : row.oee >= 75 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-5559">
                      {row.oee.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5560">
                    {row.uptime.toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5561">
                    {row.quality.toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5562">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.scrapRate <= 1.7 ? 'bg-green-100 text-green-700' : row.scrapRate <= 2.0 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-5563">
                      {row.scrapRate.toFixed(1)}%
                    </span>
                  </td>
                </tr>)}
              {/* Average Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-5564">
                <td className="py-3 px-4 text-sm text-gray-900" colSpan={2} data-id="element-5565">
                  AVERAGE
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5566">
                  44.3
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5567">
                  45.0
                </td>
                <td className="py-3 px-4 text-center" data-id="element-5568">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5569">
                    101.6%
                  </span>
                </td>
                <td className="py-3 px-4 text-center" data-id="element-5570">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5571">
                    14.5
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5572">
                  3.2
                </td>
                <td className="py-3 px-4 text-center" data-id="element-5573">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5574">
                    91.5%
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-5575">
                  3,633
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-5576">
                  3,667
                </td>
                <td className="py-3 px-4 text-center" data-id="element-5577">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5578">
                    76.7%
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5579">
                  87.8%
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5580">
                  96.1%
                </td>
                <td className="py-3 px-4 text-center" data-id="element-5581">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5582">
                    1.9%
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-5583">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-5584">
            <div className="text-center" data-id="element-5585">
              <div className="text-gray-600" data-id="element-5586">Best Cycle Efficiency</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5587">
                Line 2, Shift B
              </div>
              <div className="text-xs text-gray-500" data-id="element-5588">109.8%</div>
            </div>
            <div className="text-center" data-id="element-5589">
              <div className="text-gray-600" data-id="element-5590">Best Changeover</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5591">
                Line 2, Shift B
              </div>
              <div className="text-xs text-gray-500" data-id="element-5592">10 min</div>
            </div>
            <div className="text-center" data-id="element-5593">
              <div className="text-gray-600" data-id="element-5594">Best Operator Eff</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5595">
                Line 2, Shift B
              </div>
              <div className="text-xs text-gray-500" data-id="element-5596">98%</div>
            </div>
            <div className="text-center" data-id="element-5597">
              <div className="text-gray-600" data-id="element-5598">Avg Changeovers</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-5599">3.2</div>
              <div className="text-xs text-gray-500" data-id="element-5600">per shift</div>
            </div>
            <div className="text-center" data-id="element-5601">
              <div className="text-gray-600" data-id="element-5602">Focus Area</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-5603">
                Line 3, Shift C
              </div>
              <div className="text-xs text-gray-500" data-id="element-5604">needs improvement</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default LineLevelPerformanceDashboard;